import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';

export interface ApiCategory {
  id: string;
  name: string;
  icon: string;
  color: string;
  active: boolean;
}

export interface ApiKeyArea {
  id: string;
  name: string;
  icon: string;
  description: string;
  active: boolean;
}

export interface ApiWorkflowStep {
  id: string;
  title: string;
  description: string;
  assignee: string;
  icon: string;
  comments: string;
  status: string;
  sortOrder: number;
}

export interface ApiWorkflow {
  id: string;
  name: string;
  categoryId: string;
  icon: string;
  color: string;
  status: string;
  category?: ApiCategory;
  steps: ApiWorkflowStep[];
  keyAreas: { keyAreaId: string; keyArea: ApiKeyArea }[];
}

export function useWorkflowApi() {
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [keyAreas, setKeyAreas] = useState<ApiKeyArea[]>([]);
  const [workflows, setWorkflows] = useState<ApiWorkflow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [cats, areas, wfs] = await Promise.all([
        apiClient<ApiCategory[]>('/workflows/categories').catch(() => []),
        apiClient<ApiKeyArea[]>('/workflows/key-areas').catch(() => []),
        apiClient<ApiWorkflow[]>('/workflows').catch(() => []),
      ]);
      setCategories(Array.isArray(cats) ? cats : []);
      setKeyAreas(Array.isArray(areas) ? areas : []);
      setWorkflows(Array.isArray(wfs) ? wfs : []);
    } catch { /* fallback to empty */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Categories
  const createCategory = async (data: { name: string; icon: string; color: string }) => {
    await apiClient('/workflows/categories', { method: 'POST', body: data });
    fetchAll();
  };
  const updateCategory = async (id: string, data: Partial<ApiCategory>) => {
    await apiClient(`/workflows/categories/${id}`, { method: 'PATCH', body: data });
    fetchAll();
  };
  const deleteCategory = async (id: string) => {
    await apiClient(`/workflows/categories/${id}`, { method: 'DELETE' });
    fetchAll();
  };

  // Key Areas
  const createKeyArea = async (data: { name: string; icon: string; description: string }) => {
    await apiClient('/workflows/key-areas', { method: 'POST', body: data });
    fetchAll();
  };
  const updateKeyArea = async (id: string, data: Partial<ApiKeyArea>) => {
    await apiClient(`/workflows/key-areas/${id}`, { method: 'PATCH', body: data });
    fetchAll();
  };
  const deleteKeyArea = async (id: string) => {
    await apiClient(`/workflows/key-areas/${id}`, { method: 'DELETE' });
    fetchAll();
  };

  // Workflows
  const createWorkflow = async (data: { name: string; categoryId: string; icon: string; color: string; keyAreaIds?: string[] }) => {
    const wf = await apiClient<ApiWorkflow>('/workflows', { method: 'POST', body: data });
    fetchAll();
    return wf;
  };
  const updateWorkflow = async (id: string, data: { name?: string; status?: string; keyAreaIds?: string[] }) => {
    await apiClient(`/workflows/${id}`, { method: 'PATCH', body: data });
    fetchAll();
  };
  const deleteWorkflow = async (id: string) => {
    await apiClient(`/workflows/${id}`, { method: 'DELETE' });
    fetchAll();
  };

  // Steps
  const addStep = async (workflowId: string, data: { title: string; description?: string; assignee?: string; comments?: string }) => {
    await apiClient(`/workflows/${workflowId}/steps`, { method: 'POST', body: data });
    fetchAll();
  };
  const updateStep = async (stepId: string, data: { title?: string; description?: string; assignee?: string; icon?: string; comments?: string; status?: string }) => {
    await apiClient(`/workflows/steps/${stepId}`, { method: 'PATCH', body: data });
    fetchAll();
  };
  const deleteStep = async (stepId: string) => {
    await apiClient(`/workflows/steps/${stepId}`, { method: 'DELETE' });
    fetchAll();
  };

  // Step Reordering
  const reorderSteps = async (workflowId: string, stepIds: string[]) => {
    await apiClient(`/workflows/${workflowId}/steps/reorder`, { method: 'PATCH', body: { stepIds } });
    fetchAll();
  };

  return {
    categories, keyAreas, workflows, loading,
    createCategory, updateCategory, deleteCategory,
    createKeyArea, updateKeyArea, deleteKeyArea,
    createWorkflow, updateWorkflow, deleteWorkflow,
    addStep, updateStep, deleteStep, reorderSteps,
    refresh: fetchAll,
  };
}
